import React, { createContext, useState } from "react";
import { toast } from "react-toastify";

import {
  dispatchSoftCopyApi,
  getReadyForDispatchTranscriptsApi,
  getRequireApprovalTranscriptsApi,
  getRequireDigitizationTranscriptsApi,
  getRequireReviewTranscriptsApi,
  getRequireSigningTranscriptsApi,
  markTranscriptAsReadyForDispatchApi,
  markTranscriptAsRequireApprovalApi,
  markTranscriptAsRequireReviewApi,
  markTranscriptAsRequireSigningApi,
  previewResultApi,
} from "../api/Reads";
import { dispatchHardCopyApi, flagTranscriptApi } from "../api/Writes";

export const TranscriptManagementContext = createContext();

const TranscriptManagementProvider = ({ children }) => {
  const [requireDigitizationTranscripts, setRequireDigitizationTranscripts] =
    useState();
  const [requireReviewTranscripts, setRequireReviewTranscripts] = useState();
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [requireApprovalTranscripts, setRequireApprovalTranscripts] =
    useState();
  const [requireSigningTranscripts, setRequireSigningTranscripts] = useState();
  const [readyForDispatchTranscripts, setReadyForDispatchTranscripts] =
    useState();

  const [regNoFromRedirect, setRegNoFromRedirect] = useState("");
  const [resultPreview, setResultPreview] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  //get information for result preview
  const previewResult = async (regNo) => {
    try {
      let response = await previewResultApi(regNo);
      if (response.success) {
        setResultPreview(response.data);
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Student not found");
    }
  };
  //flag a request
  const flagRequest = async (param) => {
    try {
      let response = await flagTranscriptApi(param);
      if (response.success) {
        toast.success("Transcript flagged successfully");
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Oops, Something went wrong");
    }
  };

  //UNDIGITIZED REQUESTS
  //get all transcripts that require digitization
  const getRequireDigitizationTranscripts = async () => {
    try {
      let response = await getRequireDigitizationTranscriptsApi();
      if (response.success) {
        setRequireDigitizationTranscripts(response.data);
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Oops, Something went wrong");
    }
  };
  //mark digitized transcript as ready fro review
  const markTranscriptAsRequireReview = async (orderNo) => {
    try {
      let response = await markTranscriptAsRequireReviewApi(orderNo);
      if (response.success) {
        toast.success("Transcript successfully sent for Reviewal");
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Oops, Something went wrong");
    }
  };

  //DIGITIZED REQUESTS
  //get all transcripts that require review
  const getRequireReviewTranscripts = async (requestType, pageNumber = 0, pageSize = 10, departmentId = 0, searchParams = "") => {
    setIsLoading(true);
    try {
      let response = await getRequireReviewTranscriptsApi(requestType, pageNumber, pageSize, departmentId, searchParams);
      if (response.success) {
        // Handle the correct response structure: data.records
        const data = response.data.records || response.data.items || response.data || [];
        setRequireReviewTranscripts(Array.isArray(data) ? data : []);
        const totalCount = response.data.totalRecords || response.data.totalCount || (Array.isArray(data) ? data.length : 0);
        setTotalItems(totalCount);
        setTotalPages(typeof response.data.totalPages === 'number' ? response.data.totalPages + 1 : (pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1));
        setCurrentPage(pageNumber);
        setIsLoading(false);
      } else {
        toast.error(response.message || "Oops, Something went wrong");
        setRequireReviewTranscripts([]);
        setIsLoading(false);
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Oops, Something went wrong");
      setRequireReviewTranscripts([]);
      setIsLoading(false);
    }
  };
  //mark digitized transcript as ready fro approval
  const markTranscriptAsRequireApproval = async (orderNo) => {
    try {
      let response = await markTranscriptAsRequireApprovalApi(orderNo);
      if (response.success) {
        toast.success("Transcript successfully sent for Approval");
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Oops, Something went wrong");
    }
  };

  //AWAITING SIGNING REQUESTS
  //get all transcripts that require signing
  const getRequireSigningTranscripts = async () => {
    try {
      let response = await getRequireSigningTranscriptsApi();
      if (response.success) {
        setRequireSigningTranscripts(response.data);
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Oops, Something went wrong");
    }
  };
  //mark digitized transcript as require signing
  const markTranscriptAsRequireSigning = async (orderNo) => {
    try {
      let response = await markTranscriptAsRequireSigningApi(orderNo);
      if (response.success) {
        toast.success("Transcript successfully sent for Signing");
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Oops, Something went wrong");
    }
  };

  //AWAITING DISPATCH
  //get all transcripts that require approval
  const getRequireApprovalTranscripts = async () => {
    try {
      let response = await getRequireApprovalTranscriptsApi();
      if (response.success) {
        setRequireApprovalTranscripts(response.data);
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Oops, Something went wrong");
    }
  };
  //mark digitized transcript as ready fro approval
  const markTranscriptAsReadyForDispatch = async (orderNo) => {
    try {
      let response = await markTranscriptAsReadyForDispatchApi(orderNo);
      if (response.success) {
        toast.success("Transcript successfully sent for Disptach");
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { title } = response?.data;
      toast.error(title || "Oops, Something went wrong");
    }
  };

  //DISPATCHED TRANSCRIPTS
  //get all transcripts that are ready for dispatch
  const getReadyForDispatchTranscripts = async (requestType, pageNumber = 1, pageSize = 12) => {
    setIsLoading(true);
    try {
      let response = await getReadyForDispatchTranscriptsApi(requestType, pageNumber, pageSize);
      if (response.success) {
        if (response.data && response.data.records) {
          setReadyForDispatchTranscripts(response.data.records);
          setTotalItems(response.data.totalRecords || 0);
          setTotalPages(response.data.totalPages || 0);
          setCurrentPage(response.data.currentPage - 1);
        } else {
          setReadyForDispatchTranscripts(response.data || []);
          setTotalItems(response.data?.length || 0);
          setTotalPages(1);
          setCurrentPage(0);
        }
        setIsLoading(false);
      } else {
        toast.error(response.message || "Oops, Something went wrong");
        setReadyForDispatchTranscripts([]);
        setIsLoading(false);
      }
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.title || "Oops, Something went wrong";
      toast.error(message);
      setReadyForDispatchTranscripts([]);
      setIsLoading(false);
    }
  };

  //dispatch hard copy
  const dispatchHardCopy = async (param) => {
    try {
      let response = await dispatchHardCopyApi(param);
      if (response.success) {
        toast.success("Dispatched Successfully");
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { message } = response?.data;
      toast.error(message || "Oops, Something went wrong");
    }
  };

  //dispatch Soft copy
  const dispatchSoftCopy = async (orderNo, requestType) => {
    try {
      let response = await dispatchSoftCopyApi(orderNo, requestType);
      if (response.success) {
        toast.success("Dispatched Successfully");
      } else {
        toast.error(response.message || "Oops, Something went wrong");
      }
    } catch ({ response }) {
      const { message } = response?.data;
      toast.error(message || "Oops, Something went wrong");
    }
  };

  return (
    <TranscriptManagementContext.Provider
      value={{
        requireDigitizationTranscripts,
        getRequireDigitizationTranscripts,
        markTranscriptAsRequireReview,
        requireReviewTranscripts,
        getRequireReviewTranscripts,
        totalPages,
        totalItems,
        currentPage,
        markTranscriptAsRequireApproval,
        requireApprovalTranscripts,
        getRequireApprovalTranscripts,
        markTranscriptAsReadyForDispatch,
        requireSigningTranscripts,
        getRequireSigningTranscripts,
        markTranscriptAsRequireSigning,
        readyForDispatchTranscripts,
        getReadyForDispatchTranscripts,
        dispatchHardCopy,
        dispatchSoftCopy,
        regNoFromRedirect,
        setRegNoFromRedirect,
        resultPreview,
        previewResult,
        flagRequest,
        isLoading,
      }}
    >
      {children}
    </TranscriptManagementContext.Provider>
  );
};
export default TranscriptManagementProvider;
